/* eslint-disable eol-last */
/* eslint-disable no-unused-vars */
/* eslint-disable no-alert */
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormikContext } from "formik";
import { useParams } from "react-router-dom";

import { deleteShares } from "state/slices/tables/sharesAllocationTable";
import {
  getManualAllocation,
  clearManualAllocation,
} from "state/slices/manualShareAllocation";
import ReactTable from "components/lib/Shared/ReactTable";
import Tag from "components/lib/Shared/Tag";
import { roundNumber } from "utils";
import Button from "components/lib/Shared/Button";

const SharesAllocationTable = ({
  setEditableShareAlloc,
  isCorporate,
  resolvedFields,
  showActionCol = true,
  isInManualUpdate,
  transactions,
  row,
  onEditManualAllocation,
  onClearManualAllocation,
  showEditOnly = false, // New prop to show only edit functionality
  changesListData,
  initiatedProcessType, // NEW: to detect share transfer
}) => {
  const dispatch = useDispatch();
  const { setFieldValue, values } = useFormikContext();
  const { companyId } = useParams();
  const { shares } = useSelector((state) => state.sharesAllocationTableSlice);
  const { shares: nominalShareCapital } = useSelector(
    (state) => state.sharesTableSlice
  );

  // Detect if this is a share transfer
  const isShareTransfer = initiatedProcessType === "shareTransfer";

  // Derive transaction data for manual update mode
  const manualTx = useMemo(() => {
    if (!isInManualUpdate || !transactions) return null;
    if (Array.isArray(transactions)) {
      return (
        transactions.find((t) => t?.transactionType === "MANUAL") ||
        transactions[0] ||
        null
      );
    }
    return transactions;
  }, [isInManualUpdate, transactions]);

  // Get allotment data from Redux
  const manualAllocationData = useSelector((state) =>
    getManualAllocation(state, companyId, row?.original?._id, manualTx?._id)
  );

  // Construct table data from form data (supports multiple share classes)
  const tableData = useMemo(() => {
    // If changesListData is provided, use it directly (for changes list view)
    if (changesListData && changesListData.length > 0) {
      return changesListData;
    }

    // For manual updates, prioritize form data (sharesAllotted) as it supports multiple classes
    if (isInManualUpdate) {
      // Get data from form values first (supports multiple classes)
      const formShares = values?.sharesAllotted || [];
      if (formShares.length > 0) {
        return formShares;
      }

      // Fallback to transaction allocations if no form data
      if (manualTx?.shareCapitalChanges) {
        const allocations = [];
        manualTx.shareCapitalChanges.forEach((change) => {
          if (Array.isArray(change.allocations)) {
            change.allocations.forEach((alloc) => {
              if (String(alloc.officialId) === String(row?.original?._id)) {
                allocations.push({
                  id: `${change._id}-${alloc.officialId}-${change.classOfShares}`,
                  classOfShares: change.classOfShares,
                  allottedShares: Number(alloc.allocatedShares || 0),
                  sharesValue:
                    Number(change.offerPricePerShare || 0) -
                    Number(change.premiumPerShare || 0),
                  paidUpShares: Number(alloc.paidUpShares || 0),
                  sharePremium: Number(alloc.sharePremium || 0),
                  nominalValue:
                    Number(alloc.allocatedShares || 0) *
                    (Number(change.offerPricePerShare || 0) -
                      Number(change.premiumPerShare || 0)),
                  votingRights:
                    Number(alloc.allocatedShares || 0) *
                    (nominalShareCapital.find(
                      (ns) => ns.sharesCategory === change.classOfShares
                    )?.votesPerShare || 0),
                });
              }
            });
          }
        });
        return allocations;
      }

      // Fallback to Redux data (single class legacy support)
      if (manualAllocationData) {
        return [
          {
            id: `${manualTx?._id}-${row?.original?._id}-${manualAllocationData.classOfShares}`,
            classOfShares: manualAllocationData.classOfShares,
            allottedShares: Number(manualAllocationData.allottedShares || 0),
            sharesValue: Number(manualAllocationData.sharesValue || 0),
            paidUpShares: Number(manualAllocationData.paidUpShares || 0),
            sharePremium: Number(manualAllocationData.sharePremium || 0),
            nominalValue:
              Number(manualAllocationData.allottedShares || 0) *
              Number(manualAllocationData.sharesValue || 0),
            votingRights:
              Number(manualAllocationData.allottedShares || 0) *
              (nominalShareCapital.find(
                (ns) => ns.sharesCategory === manualAllocationData.classOfShares
              )?.votesPerShare || 0),
          },
        ];
      }
    }

    // Non-manual mode
    return shares;
  }, [
    changesListData,
    isInManualUpdate,
    values?.sharesAllotted,
    manualTx,
    shares,
    row,
    nominalShareCapital,
    manualAllocationData,
  ]);

  const { sumOfNominalValue, sumOfVotingRights } = tableData.reduce(
    (acc, share) => ({
      sumOfNominalValue:
        acc.sumOfNominalValue + Number(share.nominalValue || 0),
      sumOfVotingRights:
        acc.sumOfVotingRights + Number(share.votingRights || 0),
    }),
    { sumOfNominalValue: 0, sumOfVotingRights: 0 }
  );

  useEffect(() => {
    setFieldValue("sharesAllotted", tableData);
  }, [tableData, setFieldValue]);

  useEffect(() => {
    // Always update beneficial ownership calculations when share data changes
    // This ensures percentages are updated after edits in both manual and non-manual modes

    const getMaxPercentShareholding = () => {
      const nominalShareCapitalSum = nominalShareCapital.reduce(
        (acc, share) => acc + Number(share.sharesNumber || 0),
        0
      );
      if (nominalShareCapitalSum === 0) return 0;
      return roundNumber((sumOfNominalValue / nominalShareCapitalSum) * 100, 2);
    };

    const getMaxPercentVotingRights = () => {
      const allVotingRights = nominalShareCapital.reduce(
        (acc, share) => acc + Number(share.votingRights || 0),
        0
      );
      if (allVotingRights === 0) return 0;
      return roundNumber((sumOfVotingRights / allVotingRights) * 100, 2);
    };

    if (nominalShareCapital.length > 0) {
      const maxPercentShareholding = getMaxPercentShareholding();
      const maxPercentVotingRights = getMaxPercentVotingRights();
      setFieldValue(
        "beneficialOwnershipForm.maxPercentShareholding",
        maxPercentShareholding
      );
      setFieldValue(
        "beneficialOwnershipForm.maxPercentVotingRights",
        maxPercentVotingRights
      );
      if (isCorporate) {
        setFieldValue(
          "beneficialOwnershipForm.indirectPercentShareholding",
          maxPercentShareholding
        );
        setFieldValue(
          "beneficialOwnershipForm.indirectPercentVotingRights",
          maxPercentVotingRights
        );
      }
    }
  }, [
    sumOfNominalValue,
    sumOfVotingRights,
    nominalShareCapital,
    setFieldValue,
    isCorporate,
    tableData, // Add tableData to trigger recalculation when table changes
  ]);

  const handleShareEdit = (rowData) => {
    if (isInManualUpdate) {
      // For manual updates, find the corresponding transaction data and set as editable
      const change = manualTx?.shareCapitalChanges?.find(
        (c) => c.classOfShares === rowData.classOfShares
      );

      if (change) {
        // Create editable data structure from transaction and table data
        const editableData = {
          id: rowData.id,
          classOfShares: rowData.classOfShares,
          allottedShares: rowData.allottedShares,
          paidUpShares: rowData.paidUpShares,
          sharePrice: Number(change.offerPricePerShare || 0),
          sharePremium: rowData.sharePremium,
          sharesValue: rowData.sharesValue,
          // Add effective date if available
          effectiveDate: change.dateOfOffer || "",
        };
        setEditableShareAlloc(editableData);

        if (onEditManualAllocation) {
          onEditManualAllocation();
        }
      }
      return;
    }
    setEditableShareAlloc(rowData);
  };

  const handleShareDelete = (rowData) => {
    if (isInManualUpdate) {
      // For manual updates, remove only the specific share class from table
      if (
        window.confirm(
          `Are you sure you want to clear the allocation for ${rowData.classOfShares}?`
        )
      ) {
        // Remove the specific class from the form data
        const currentFormShares = values?.sharesAllotted || [];
        const updatedShares = currentFormShares.filter(
          (share) => share.classOfShares !== rowData.classOfShares
        );
        setFieldValue("sharesAllotted", updatedShares);

        // Clear any related Redux allocation data for this specific class
        dispatch(
          clearManualAllocation({
            companyId,
            officialId: row?.original?._id,
            transactionId: manualTx?._id,
            classOfShares: rowData.classOfShares, // Add class filter if supported by Redux action
          })
        );

        if (onClearManualAllocation) {
          onClearManualAllocation();
        }
      }
      return;
    }

    if (window.confirm("Are you sure you want to delete this row?")) {
      setEditableShareAlloc(null);
      dispatch(deleteShares(rowData));
      setFieldValue("sharesAllotted", [
        ...tableData.filter((share) => share.id !== rowData.id),
      ]);

      // Only clear beneficial ownership if no shares remain
      const remainingShares = tableData.filter(
        (share) => share.id !== rowData.id
      );
      if (remainingShares.length === 0) {
        setFieldValue("beneficialOwnershipForm", {
          ...values?.beneficialOwnershipForm,
          directPercentShareholding: "",
          indirectPercentShareholding: "",
          directPercentVotingRights: "",
          indirectPercentVotingRights: "",
          dateOfBecomingBO: "",
        });
      }
    }
  };

  const columns = [
    {
      Header: "Class of Shares",
      Cell: ({ row }) => {
        if (row.original.id === "total") return <b>Total</b>;
        return row.original.classOfShares;
      },
    },
    { Header: "Number of Shares", accessor: "allottedShares" },
    {
      Header: "Value of Shares",
      Cell: ({ row }) => {
        if (row.original.id !== "total") {
          return (
            <div className="inline-flex items-baseline">
              <span className="px-1 text-xs font-medium">KES</span>{" "}
              <Tag>{roundNumber(row.original.sharesValue)}</Tag>
            </div>
          );
        }
        return "";
      },
    },
    { Header: "Paid Up Shares", accessor: "paidUpShares" },
    {
      Header: "Premium Paid",
      Cell: ({ row }) => {
        if (row.original.id !== "total") {
          let premiumValue = row.original.sharePremium;

          // For share transfers, use premiumPerShare from transaction instead of row data
          if (isShareTransfer && manualTx?.shareCapitalChanges) {
            const change = manualTx.shareCapitalChanges.find(
              (c) => c.classOfShares === row.original.classOfShares
            );
            if (change && change.premiumPerShare !== undefined) {
              premiumValue = Number(change.premiumPerShare || 0);
            }
          }

          return (
            <div className="inline-flex items-baseline">
              <span className="px-1 text-xs font-medium">KES</span>{" "}
              <Tag>{roundNumber(premiumValue)}</Tag>
            </div>
          );
        }
        return "";
      },
    },
    {
      Header: "Nominal Value",
      Cell: ({ row }) => {
        return (
          <div className="inline-flex items-baseline">
            <span className="px-1 text-xs font-medium">KES</span>{" "}
            <Tag>{roundNumber(row.original.nominalValue)}</Tag>
          </div>
        );
      },
    },
    {
      Header: "Voting Rights",
      Cell: ({ row }) => {
        if (row.original.id === "total")
          return <b>{roundNumber(row.original.votingRights)}</b>;
        return roundNumber(row.original.votingRights);
      },
    },
    showActionCol && {
      Header: "Action",
      Cell: ({ row }) => {
        if (row.original.id === "total") return "";
        return (
          <div className="grid gap-2 p-2">
            <Button
              className="items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
              onClick={() => handleShareEdit(row.original)}
              disabled={resolvedFields.includes(row.original.classOfShares)}
            >
              Edit
            </Button>
            {!showEditOnly && (
              <Button
                onClick={() => handleShareDelete(row.original)}
                disabled={resolvedFields.includes(row.original.classOfShares)}
                className="items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:cursor-not-allowed"
              >
                {isInManualUpdate && manualAllocationData ? "Clear" : "Delete"}
              </Button>
            )}
          </div>
        );
      },
    },
  ].filter(Boolean);

  const data = [...tableData];
  if (tableData.length > 0) {
    data.push({
      id: "total",
      classOfShares: "Total",
      allottedShares: "",
      sharesValue: "",
      paidUpShares: "",
      sharePremium: "",
      nominalValue: sumOfNominalValue,
      votingRights: sumOfVotingRights,
    });
  }

  return (
    <>
      {data.length > 0 && (
        <div className="w-full bg-white shadow">
          <div className="w-full overflow-auto">
            <ReactTable columns={columns} data={data} />
          </div>
        </div>
      )}
    </>
  );
};

export default SharesAllocationTable;
